<template>
  <Teleport to="body">
    <template v-if="!isDestroy">
      <Transition :name="`popup-${animationType}-${position}`" appear>
        <div
          v-show="visible"
          :class="`popup-wrapper ${wrapperClass} ${position}`"
          :style="{ zIndex: currentZIndex }"
          @click="handleWrapperClick">
          <!-- 遮罩层 -->
          <div v-if="showMask" class="popup-mask" :style="maskStyle" />

          <!-- 弹窗内容 -->
          <div
            ref="popupRef"
            :class="['popup-content', `popup-${position}`, contentClass]"
            @click.stop>
            <!-- 默认插槽内容 -->
            <slot />
          </div>
        </div>
      </Transition>
    </template>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { PopupManager } from '.'

// 组件属性接口
export interface PopupProps {
  /** 是否显示弹窗 */
  visible?: boolean
  /** 弹窗位置 */
  position?: 'center' | 'top' | 'bottom' | 'left' | 'right'
  /** 是否显示遮罩 */
  showMask?: boolean
  /** 点击遮罩是否关闭 */
  maskClosable?: boolean
  /** 自定义包装器类名 */
  wrapperClass?: string
  /** 自定义内容类名 */
  contentClass?: string
  /** 动画类型 */
  animation?: 'fade' | 'slide' | 'scale' | 'auto'
  /** 是否锁定滚动 */
  lockScroll?: boolean
}

// 属性默认值
const props = withDefaults(defineProps<PopupProps>(), {
  visible: false,
  position: 'center',
  showMask: true,
  maskClosable: true,
  wrapperClass: '',
  contentClass: '',
  animation: 'auto',
  lockScroll: true
})

// 事件定义
const emit = defineEmits<{
  'update:visible': [visible: boolean]
  close: []
  open: []
  opened: []
  closed: []
}>()

// 响应式数据
const popupRef = ref<HTMLElement>()
const currentZIndex = ref(PopupManager.getTopZIndex() + 1)
const originalBodyOverflow = ref('')

// 计算属性
const maskStyle = computed(() => ({
  backgroundColor: 'rgba(0, 0, 0, 0.8)'
}))

// 动画类型计算
const animationType = computed(() => {
  if (props.animation !== 'auto') {
    return props.animation
  }

  // 自动选择动画类型
  switch (props.position) {
    case 'bottom':
      return 'slide'
    case 'top':
      return 'slide'
    case 'left':
      return 'slide'
    case 'right':
      return 'slide'
    case 'center':
    default:
      return 'scale'
  }
})

// 方法
const handleWrapperClick = () => {
  if (props.maskClosable) {
    handleClose()
  }
}

const handleClose = () => {
  emit('update:visible', false)
  emit('close')
}

const lockBodyScroll = () => {
  if (!props.lockScroll) return
  originalBodyOverflow.value = document.body.style.overflow
  document.body.style.overflow = 'hidden'
}

const unlockBodyScroll = () => {
  if (!props.lockScroll) return
  document.body.style.overflow = originalBodyOverflow.value
}

const isDestroy = ref(true)

// 监听器
watch(
  () => props.visible,
  async (newVisible) => {
    if (newVisible) {
      // 注册实例并获取 z-index
      currentZIndex.value = PopupManager.register()
      lockBodyScroll()
      emit('open')
      isDestroy.value = false
      await nextTick()
      emit('opened')
    } else {
      // 注销实例
      PopupManager.unregister(currentZIndex.value)
      unlockBodyScroll()

      setTimeout(() => {
        isDestroy.value = true
        emit('closed')
      }, 300)
    }
  },
  { immediate: true }
)

// 生命周期
onMounted(() => {
  // ESC 键关闭
  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && props.visible) {
      handleClose()
    }
  }

  document.addEventListener('keydown', handleEscape)

  onUnmounted(() => {
    document.removeEventListener('keydown', handleEscape)
    // 确保注销实例
    PopupManager.unregister(currentZIndex.value)
    unlockBodyScroll()
  })
})
</script>

<style lang="scss" scoped>
.popup-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;

  &.bottom {
    align-items: flex-end;
  }

  &.top {
    align-items: flex-start;
  }

  &.left {
    justify-content: flex-start;
  }

  &.right {
    justify-content: flex-end;
  }
}

.popup-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
}

.popup-content {
  position: relative;
  overflow: auto;
  outline: none;
  max-width: 90vw;
  max-height: 90vh;
  width: auto;
}

/* Vue Transition 动画类 */
$transition-duration: 0.2s;
$scale-transition: all $transition-duration cubic-bezier(0.68, -0.55, 0.265, 1.55);
$slide-transition: all $transition-duration cubic-bezier(0.25, 0.46, 0.45, 0.94);

.popup-fade-center-enter-active,
.popup-fade-center-leave-active,
.popup-scale-center-enter-active,
.popup-scale-center-leave-active,
.popup-slide-bottom-enter-active,
.popup-slide-bottom-leave-active,
.popup-slide-top-enter-active,
.popup-slide-top-leave-active,
.popup-slide-left-enter-active,
.popup-slide-left-leave-active,
.popup-slide-right-enter-active,
.popup-slide-right-leave-active {
  transition: all $transition-duration ease;
}

/* FADE 动画 */
.popup-fade-center-enter-from,
.popup-fade-center-leave-to {
  opacity: 0;
}

/* SCALE 动画 */
.popup-scale-center-enter-active .popup-content,
.popup-scale-center-leave-active .popup-content {
  transition: $scale-transition;
}

.popup-scale-center-enter-from .popup-content,
.popup-scale-center-leave-to .popup-content {
  opacity: 0;
  transform: scale(0.7);
}

/* SLIDE 动画 - 底部 */
.popup-slide-bottom-enter-active .popup-content,
.popup-slide-bottom-leave-active .popup-content {
  transition: $slide-transition;
}

.popup-slide-bottom-enter-from .popup-content,
.popup-slide-bottom-leave-to .popup-content {
  transform: translateY(100%);
}

/* SLIDE 动画 - 顶部 */
.popup-slide-top-enter-active .popup-content,
.popup-slide-top-leave-active .popup-content {
  transition: $slide-transition;
}

.popup-slide-top-enter-from .popup-content,
.popup-slide-top-leave-to .popup-content {
  transform: translateY(-100%);
}

/* SLIDE 动画 - 左侧 */
.popup-slide-left-enter-active .popup-content,
.popup-slide-left-leave-active .popup-content {
  transition: $slide-transition;
}

.popup-slide-left-enter-from .popup-content,
.popup-slide-left-leave-to .popup-content {
  transform: translateX(-100%);
}

/* SLIDE 动画 - 右侧 */
.popup-slide-right-enter-active .popup-content,
.popup-slide-right-leave-active .popup-content {
  transition: $slide-transition;
}

.popup-slide-right-enter-from .popup-content,
.popup-slide-right-leave-to .popup-content {
  transform: translateX(100%);
}
</style>
